import Masonry from "react-masonry-css";
import {
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { PopulatedDesignBoard, PopulatedProjectFile } from "../../__generated__/types";
import DashboardHeader from "../../components/dashboard-header";
import { MaterialHero } from "../../components/static-page-components/material-hero";
import { ProjectPreviewDialog } from "../../components/project-preview-dialog";
import { SaveToIdeaBookPopover } from "../../components/idea-books/save-to-idea-book-popover";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import { useUserContext } from "../../hooks/userContext";
import {
  DesignerFileCardApi,
  useGetDesignerFileCards,
} from "../../hooks/use-get-designer-file-cards";
import {
  DesignBoardTypeLabels,
  Pages,
  budgetRangeOptions,
  colorFamilyOptions,
  finishOptions,
  formatSpecialRequirementLabel,
  patternLabels,
  patternOptions,
  projectCategoryOptions as projectCategoryOptionsList,
  projectCategoryLabels,
  projectStyleOptions,
  projectStyleLabels,
  specialRequirementsOptions,
  squareFootageOptions,
  textureOptions,
  textureLabels,
} from "../../utils";

const PREVIEW_LIMIT = 10;
const PAGE_SIZE = 18;
const MASONRY_CARD_MIN_WIDTH = 215;
const MASONRY_GUTTER = 20;
const IMAGE_LOAD_TIMEOUT_MS = 8000;
const FILTER_STORAGE_KEY = "designer-files-public-filters";

const getSortKeyFromObjectId = (value?: string | null) => {
  if (!value) return 0;
  const hex = value.toString().slice(0, 8);
  const num = Number.parseInt(hex, 16);
  return Number.isNaN(num) ? 0 : num;
};

const mulberry32 = (seed: number) => {
  let t = seed;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), t | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const shuffleArray = <T,>(items: T[], random: () => number) => {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
};

const interleaveByGroup = <T,>(
  items: T[],
  getGroupId: (item: T) => string,
  seed: number,
  lastGroupId?: string | null
) => {
  if (items.length <= 1) return items;

  const groups = new Map<string, T[]>();
  items.forEach((item, index) => {
    const rawKey = getGroupId(item);
    const key = rawKey || `__ungrouped-${index}`;
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  });

  const keys = shuffleArray(Array.from(groups.keys()), mulberry32(seed));
  const result: T[] = [];
  let lastKey = lastGroupId ?? null;

  while (result.length < items.length && keys.length > 0) {
    let progressed = false;
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (key === lastKey && keys.length > 1) continue;
      const group = groups.get(key);
      if (!group || group.length === 0) {
        keys.splice(i, 1);
        i -= 1;
        continue;
      }
      result.push(group.shift() as T);
      lastKey = key;
      progressed = true;
      if (group.length === 0) {
        keys.splice(i, 1);
        i -= 1;
      }
    }

    if (!progressed && keys.length > 0) {
      const key = keys[0];
      const group = groups.get(key);
      if (!group || group.length === 0) {
        keys.splice(0, 1);
        continue;
      }
      result.push(group.shift() as T);
      lastKey = key;
      if (group.length === 0) {
        keys.splice(0, 1);
      }
    }
  }

  return result;
};

const appendInterleavedByGroup = <T,>(
  prev: T[],
  next: T[],
  getGroupId: (item: T) => string,
  seed: number
) => {
  if (!next.length) return prev;
  const lastGroupId = prev.length ? getGroupId(prev[prev.length - 1]) : null;
  const interleaved = interleaveByGroup(next, getGroupId, seed, lastGroupId);
  return [...prev, ...interleaved];
};

type DesignerFileType = "project" | "before-after" | "design-board";
type FilterKey = "all" | "ideas" | DesignerFileType;

type PublicDesignerCard = {
  id: string;
  fileId: string;
  fileType: DesignerFileType;
  imageId: string;
  imageUrl: string;
  altText: string;
  title: string;
  tags: string[];
  designerId: string;
  sortKey: number;
  projectCategory?: string | null;
  projectStyle?: string | null;
  budgetRange?: string | null;
  squareFootage?: string | null;
  specialRequirements?: string[] | null;
  designBoardType?: string | null;
  roomType?: string[] | null;
  pattern?: string[] | null;
  texture?: string[] | null;
  color?: string[] | null;
  finish?: string[] | null;
};


type TaggableProject = {
  projectStyle?: PopulatedProjectFile["projectStyle"] | null;
  style?: PopulatedProjectFile["style"] | null;
  projectCategory?: PopulatedProjectFile["projectCategory"] | null;
  specialRequirements?: PopulatedProjectFile["specialRequirements"] | null;
  budgetRange?: PopulatedProjectFile["budgetRange"] | null;
  squareFootage?: PopulatedProjectFile["squareFootage"] | null;
};

type TaggableDesignBoard = Pick<
  PopulatedDesignBoard,
  "type" | "roomType" | "pattern" | "texture" | "color" | "finish"
>;

type SelectedFilter = {
  type: "project" | "before-after";
  value: string;
  label: string;
};

const normalizeTagLabel = (label: string) => {
  const trimmed = label.replace(/\s*Design$/i, "").replace(/\s+/g, " ").trim();
  return trimmed || label;
};

const formatProjectCategoryLabel = (value: string | null | undefined) => {
  if (!value) return "";
  return (
    projectCategoryLabels[value as keyof typeof projectCategoryLabels] ??
    String(value)
  );
};

const getProjectTags = (project: TaggableProject) => {
  const tags: string[] = [];
  const styleValue = project.projectStyle ?? project.style;

  if (styleValue) {
    const styleLabel =
      projectStyleLabels[styleValue as keyof typeof projectStyleLabels] ??
      String(styleValue);
    tags.push(styleLabel);
  }

  if (project.projectCategory) {
    const categoryLabel = formatProjectCategoryLabel(project.projectCategory);
    if (categoryLabel) {
      tags.push(categoryLabel);
    }
  }

  (project.specialRequirements ?? []).forEach((requirement) => {
    if (!requirement) return;
    const requirementLabel = formatSpecialRequirementLabel(String(requirement));
    if (!requirementLabel) return;
    const normalizedLabel = requirementLabel.toLowerCase();
    if (normalizedLabel === "n/a" || normalizedLabel === "none") return;
    tags.push(requirementLabel);
  });

  const uniqueTags = Array.from(
    new Set(tags.map((tag) => normalizeTagLabel(tag)).filter(Boolean))
  );

  return uniqueTags.slice(0, 3);
};

const getDesignBoardTags = (board: TaggableDesignBoard) => {
  const tags: string[] = [];

  if (board.type) {
    tags.push(
      DesignBoardTypeLabels[board.type as keyof typeof DesignBoardTypeLabels] ??
        String(board.type)
    );
  }

  const roomType = board.roomType?.find(Boolean);
  if (roomType) {
    tags.push(
      projectCategoryLabels[roomType as keyof typeof projectCategoryLabels] ??
        String(roomType)
    );
  }

  const pattern = board.pattern?.find(Boolean);
  if (pattern) {
    tags.push(patternLabels[pattern as keyof typeof patternLabels] ?? String(pattern));
  }

  const texture = board.texture?.find(Boolean);
  if (texture) {
    tags.push(textureLabels[texture as keyof typeof textureLabels] ?? String(texture));
  }

  const uniqueTags = Array.from(
    new Set(tags.map((tag) => normalizeTagLabel(tag)).filter(Boolean))
  );

  return uniqueTags.slice(0, 3);
};

type ProjectFilterState = {
  projectTypes: string[];
  projectStyles: string[];
  budgetRanges: string[];
  squareFootages: string[];
  specialRequirements: string[];
};

type DesignBoardFilterState = {
  activeType: string | null;
  mood: {
    projectTypes: string[];
    projectStyles: string[];
    colors: string[];
  };
  color: {
    colors: string[];
  };
  texture: {
    textures: string[];
    finishes: string[];
  };
  pattern: {
    patterns: string[];
    colors: string[];
  };
  styleFusion: {
    patterns: string[];
    colors: string[];
  };
};

type DrawerView =
  | "root"
  | "before-after"
  | "project"
  | "design-boards"
  | "design-board-mood"
  | "design-board-color"
  | "design-board-texture"
  | "design-board-pattern"
  | "design-board-style-fusion";

const defaultProjectFilters: ProjectFilterState = {
  projectTypes: [],
  projectStyles: [],
  budgetRanges: [],
  squareFootages: [],
  specialRequirements: [],
};

const defaultDesignBoardFilters: DesignBoardFilterState = {
  activeType: null,
  mood: {
    projectTypes: [],
    projectStyles: [],
    colors: [],
  },
  color: {
    colors: [],
  },
  texture: {
    textures: [],
    finishes: [],
  },
  pattern: {
    patterns: [],
    colors: [],
  },
  styleFusion: {
    patterns: [],
    colors: [],
  },
};

const cloneProjectFilters = (filters: ProjectFilterState): ProjectFilterState => ({
  projectTypes: [...filters.projectTypes],
  projectStyles: [...filters.projectStyles],
  budgetRanges: [...filters.budgetRanges],
  squareFootages: [...filters.squareFootages],
  specialRequirements: [...filters.specialRequirements],
});

const cloneDesignBoardFilters = (
  filters: DesignBoardFilterState
): DesignBoardFilterState => ({
  activeType: filters.activeType,
  mood: {
    projectTypes: [...filters.mood.projectTypes],
    projectStyles: [...filters.mood.projectStyles],
    colors: [...filters.mood.colors],
  },
  color: {
    colors: [...filters.color.colors],
  },
  texture: {
    textures: [...filters.texture.textures],
    finishes: [...filters.texture.finishes],
  },
  pattern: {
    patterns: [...filters.pattern.patterns],
    colors: [...filters.pattern.colors],
  },
  styleFusion: {
    patterns: [...filters.styleFusion.patterns],
    colors: [...filters.styleFusion.colors],
  },
});

const toStringArray = (value: unknown) =>
  Array.isArray(value) ? value.filter(Boolean).map((entry) => String(entry)) : [];

const sanitizeProjectFilters = (value: any): ProjectFilterState => ({
  projectTypes: toStringArray(value?.projectTypes),
  projectStyles: toStringArray(value?.projectStyles),
  budgetRanges: toStringArray(value?.budgetRanges),
  squareFootages: toStringArray(value?.squareFootages),
  specialRequirements: toStringArray(value?.specialRequirements),
});

const sanitizeDesignBoardFilters = (value: any): DesignBoardFilterState => ({
  activeType: value?.activeType ? String(value.activeType) : null,
  mood: {
    projectTypes: toStringArray(value?.mood?.projectTypes),
    projectStyles: toStringArray(value?.mood?.projectStyles),
    colors: toStringArray(value?.mood?.colors),
  },
  color: {
    colors: toStringArray(value?.color?.colors),
  },
  texture: {
    textures: toStringArray(value?.texture?.textures),
    finishes: toStringArray(value?.texture?.finishes),
  },
  pattern: {
    patterns: toStringArray(value?.pattern?.patterns),
    colors: toStringArray(value?.pattern?.colors),
  },
  styleFusion: {
    patterns: toStringArray(value?.styleFusion?.patterns),
    colors: toStringArray(value?.styleFusion?.colors),
  },
});

const sortOptionsByLabel = <T extends { label: string }>(options: T[]) =>
  options.slice().sort((a, b) => a.label.localeCompare(b.label));

const toggleValue = (values: string[], value: string) => {
  if (values.includes(value)) {
    return values.filter((entry) => entry !== value);
  }
  return [...values, value];
};

const FILTER_LABELS: Record<FilterKey, string> = {
  all: "All Designer Files",
  ideas: "Ideas",
  "before-after": "Before & Afters",
  project: "Projects",
  "design-board": "Design Boards",
};

const getHomeownerTier = (subscription: unknown) => {
  const rawSubscription =
    typeof subscription === "string"
      ? subscription
      : (subscription as any)?.homeownerValue ||
        (subscription as any)?.designerValue ||
        (subscription as any)?.value ||
        (subscription as any)?.tier ||
        "";

  if (!rawSubscription) return "basic";
  if (rawSubscription === "Free" || rawSubscription === "basic") return "basic";
  if (rawSubscription === "GuildMonthly" || rawSubscription === "Guild")
    return "Guild";
  if (
    rawSubscription === "GuildAnnually" ||
    rawSubscription === "GuildPlus" ||
    rawSubscription === "Guild+"
  )
    return "GuildPlus";
  if (
    rawSubscription === "StyleFileMonthly" ||
    rawSubscription === "StyleFileAnnually"
  )
    return "Guild";

  return "basic";
};

export const DesignerFilesPublicPage = () => {
  const { user } = useUserContext();
  const { canAccessAdminFeatures } = useEffectiveRole();
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeownerDesignerFilesView =
    location.pathname === Pages.DESIGNER_FILES_HOMEOWNER;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const shuffleSeedRef = useRef(Math.floor(Math.random() * 1_000_000));
  const shuffleStepRef = useRef(0);
  const locationState = location.state as
    | { designerId?: string; filter?: FilterKey }
    | null;
  const searchParams = new URLSearchParams(location.search);
  const filterParam = searchParams.get("filter");
  const designerIdParam = searchParams.get("designerId");
  const designerIdFilter = (locationState?.designerId ?? designerIdParam ?? "").trim();
  const allowedFilters: FilterKey[] = [
    "all",
    "ideas",
    "before-after",
    "project",
    "design-board",
  ];
  const normalizedFilter = (() => {
    const candidate = locationState?.filter ?? filterParam;
    if (!candidate) return null;
    const normalized = String(candidate);
    return allowedFilters.includes(normalized as FilterKey)
      ? (normalized as FilterKey)
      : null;
  })();
  const {
    getProjectFileCards,
    getBeforeAfterFileCards,
    getDesignBoardFileCards,
  } = useGetDesignerFileCards();
  const [projectCards, setProjectCards] = useState<PublicDesignerCard[]>([]);
  const [beforeAfterCards, setBeforeAfterCards] = useState<PublicDesignerCard[]>([]);
  const [designBoardCards, setDesignBoardCards] = useState<PublicDesignerCard[]>([]);
  const [allCardsState, setAllCardsState] = useState<PublicDesignerCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterKey>(
    normalizedFilter ?? "all"
  );
  const [projectFilters, setProjectFilters] =
    useState<ProjectFilterState>(defaultProjectFilters);
  const [beforeAfterFilters, setBeforeAfterFilters] =
    useState<ProjectFilterState>(defaultProjectFilters);
  const [designBoardFilters, setDesignBoardFilters] =
    useState<DesignBoardFilterState>(defaultDesignBoardFilters);
  const [draftProjectFilters, setDraftProjectFilters] =
    useState<ProjectFilterState>(defaultProjectFilters);
  const [draftBeforeAfterFilters, setDraftBeforeAfterFilters] =
    useState<ProjectFilterState>(defaultProjectFilters);
  const [draftDesignBoardFilters, setDraftDesignBoardFilters] =
    useState<DesignBoardFilterState>(defaultDesignBoardFilters);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerView, setDrawerView] = useState<DrawerView>("root");
  const [expandedSection, setExpandedSection] = useState<string>("projectType");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === "undefined" ? 1200 : window.innerWidth
  );
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [hasMoreProjectCards, setHasMoreProjectCards] = useState(true);
  const [hasMoreBeforeAfterCards, setHasMoreBeforeAfterCards] = useState(true);
  const [hasMoreDesignBoardCards, setHasMoreDesignBoardCards] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [previewFileType, setPreviewFileType] =
    useState<DesignerFileType | null>(null);
  const [saveAnchorEl, setSaveAnchorEl] = useState<HTMLElement | null>(null);
  const [saveCard, setSaveCard] = useState<PublicDesignerCard | null>(null);
  const [trackImages, setTrackImages] = useState(true);
  const [imagesReady, setImagesReady] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const projectCardsRef = useRef<PublicDesignerCard[]>([]);
  const beforeAfterCardsRef = useRef<PublicDesignerCard[]>([]);
  const designBoardCardsRef = useRef<PublicDesignerCard[]>([]);
  const allCardsRef = useRef<PublicDesignerCard[]>([]);
  const isBatchLoadingRef = useRef(false);
  const fetchKeyRef = useRef<string | null>(null);
  const isLoadingMoreRef = useRef(false);
  const imageLoadSetRef = useRef<Set<string>>(new Set());
  const imageTotalRef = useRef(0);
  const loadedImageIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (!normalizedFilter && !designerIdFilter && parsed?.activeFilter) {
          setActiveFilter(parsed.activeFilter as FilterKey);
        }
        if (parsed?.projectFilters) {
          setProjectFilters(sanitizeProjectFilters(parsed.projectFilters));
        }
        if (parsed?.beforeAfterFilters) {
          setBeforeAfterFilters(
            sanitizeProjectFilters(parsed.beforeAfterFilters)
          );
        }
        if (parsed?.designBoardFilters) {
          setDesignBoardFilters(
            sanitizeDesignBoardFilters(parsed.designBoardFilters)
          );
        }
      } catch (error) {
        console.warn("Failed to restore designer files filters", error);
      }
    }
    setFiltersHydrated(true);
  }, [designerIdFilter, normalizedFilter]);

  useEffect(() => {
    if (typeof window === "undefined" || !filtersHydrated) return;
    const payload = {
      activeFilter,
      projectFilters,
      beforeAfterFilters,
      designBoardFilters,
    };
    window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(payload));
  }, [
    activeFilter,
    projectFilters,
    beforeAfterFilters,
    designBoardFilters,
    filtersHydrated,
  ]);

  const isHomeowner = (user?.accountType?.toLowerCase() || "") === "homeowner";
  const subscriptionTier = useMemo(
    () => getHomeownerTier(user?.subscription),
    [user?.subscription]
  );
  const paymentStatus = ((user as any)?.paymentStatus || "")
    .toString()
    .toLowerCase();
  const hasPaidStatus = paymentStatus === "paid" || paymentStatus === "completed";
  const hasAdminHomeownerViewAccess =
    isHomeownerDesignerFilesView && canAccessAdminFeatures;
  const hasFullAccess =
    hasAdminHomeownerViewAccess ||
    (isHomeowner &&
      (subscriptionTier === "Guild" ||
        subscriptionTier === "GuildPlus" ||
        hasPaidStatus));
  const canSaveToIdeaBooks = isHomeowner && hasFullAccess;

  const maxCards = hasFullAccess ? Number.POSITIVE_INFINITY : PREVIEW_LIMIT;
  const getGroupKey = useCallback(
    (card: PublicDesignerCard) => `${card.fileType}-${card.fileId || card.id}`,
    []
  );
  const nextShuffleSeed = useCallback(() => {
    shuffleStepRef.current += 1;
    return shuffleSeedRef.current + shuffleStepRef.current;
  }, []);
  const appendCards = useCallback(
    (prev: PublicDesignerCard[], cards: PublicDesignerCard[]) => {
      if (!cards.length) return prev;
      if (designerIdFilter) {
        return [...prev, ...cards];
      }
      return appendInterleavedByGroup(prev, cards, getGroupKey, nextShuffleSeed());
    },
    [designerIdFilter, getGroupKey, nextShuffleSeed]
  );

  useEffect(() => {
    projectCardsRef.current = projectCards;
  }, [designerIdFilter, projectCards]);

  useEffect(() => {
    beforeAfterCardsRef.current = beforeAfterCards;
  }, [beforeAfterCards]);

  useEffect(() => {
    designBoardCardsRef.current = designBoardCards;
  }, [designBoardCards]);

  useEffect(() => {
    allCardsRef.current = allCardsState;
  }, [allCardsState]);

  useEffect(() => {
    const existingIds = new Set(allCardsRef.current.map((card) => card.id));
    const appended: PublicDesignerCard[] = [];
    const pushIfNew = (card: PublicDesignerCard) => {
      if (existingIds.has(card.id)) return;
      existingIds.add(card.id);
      appended.push(card);
    };
    projectCards.forEach(pushIfNew);
    beforeAfterCards.forEach(pushIfNew);
    designBoardCards.forEach(pushIfNew);
    if (appended.length) {
      setAllCardsState((prev) => appendCards(prev, appended));
    }
  }, [appendCards, beforeAfterCards, designBoardCards, projectCards]);

  useEffect(() => {
    if (!isLoading) return;
    setTrackImages(true);
    setImagesReady(false);
    imageLoadSetRef.current = new Set();
    imageTotalRef.current = 0;
  }, [isLoading]);

  const mapProjectCard = useCallback((card: DesignerFileCardApi) => {
    if (card.published === false) return null;
    const fileId = String(card.fileId ?? "").trim();
    if (!fileId) return null;
    const imageUrl = card.image?.url ?? card.image?.path ?? "";
    if (!imageUrl) return null;
    const imageId = String(card.image?._id ?? "");
    const imageTitle = card.image?.title?.trim();
    const title = imageTitle || card.title?.trim() || "Project Name";
    const tags = getProjectTags({
      projectStyle: card.projectStyle as TaggableProject["projectStyle"],
      style: card.style as TaggableProject["style"],
      projectCategory: card.projectCategory as TaggableProject["projectCategory"],
      specialRequirements: (card.specialRequirements ??
        []) as TaggableProject["specialRequirements"],
    });

    return {
      id: card.id || `project-${fileId}-${card.image?._id ?? imageUrl}`,
      fileId,
      fileType: "project",
      imageId,
      imageUrl,
      altText: card.image?.title ?? title,
      title,
      tags,
      designerId: String(card.designerId ?? ""),
      sortKey:
        typeof card.displayOrder === "number"
          ? card.displayOrder
          : getSortKeyFromObjectId(imageId || fileId),
      projectCategory: card.projectCategory ?? null,
      projectStyle: card.projectStyle ?? card.style ?? null,
      budgetRange: card.budgetRange ?? null,
      squareFootage: card.squareFootage ?? null,
      specialRequirements: (card.specialRequirements ?? [])
        .filter(Boolean)
        .map((value) => String(value)),
    } as PublicDesignerCard;
  }, []);

  const mapBeforeAfterCard = useCallback((card: DesignerFileCardApi) => {
    if (card.published === false) return null;
    const fileId = String(card.fileId ?? "").trim();
    if (!fileId) return null;
    const imageUrl = card.image?.url ?? card.image?.path ?? "";
    if (!imageUrl) return null;
    const imageId = String(card.image?._id ?? "");
    const imageTitle = card.image?.title?.trim();
    const title = imageTitle || card.title?.trim() || "Project Name";
    const tags = getProjectTags({
      projectStyle: card.projectStyle as TaggableProject["projectStyle"],
      style: card.style as TaggableProject["style"],
      projectCategory: card.projectCategory as TaggableProject["projectCategory"],
      specialRequirements: (card.specialRequirements ??
        []) as TaggableProject["specialRequirements"],
    });

    return {
      id: card.id || `before-after-${fileId}-${card.image?._id ?? imageUrl}`,
      fileId,
      fileType: "before-after",
      imageId,
      imageUrl,
      altText: card.image?.title ?? title,
      title,
      tags,
      designerId: String(card.designerId ?? ""),
      sortKey:
        typeof card.displayOrder === "number"
          ? card.displayOrder
          : getSortKeyFromObjectId(imageId || fileId),
      projectCategory: card.projectCategory ?? null,
      projectStyle: card.projectStyle ?? card.style ?? null,
      budgetRange: card.budgetRange ?? null,
      squareFootage: card.squareFootage ?? null,
      specialRequirements: (card.specialRequirements ?? [])
        .filter(Boolean)
        .map((value) => String(value)),
    } as PublicDesignerCard;
  }, []);

  const mapDesignBoardCard = useCallback((card: DesignerFileCardApi) => {
    if (card.published === false) return null;
    const fileId = String(card.fileId ?? "").trim();
    if (!fileId) return null;
    const imageUrl = card.image?.url ?? card.image?.path ?? "";
    if (!imageUrl) return null;
    const imageId = String(card.image?._id ?? "");
    const imageTitle = card.image?.title?.trim();
    const title = imageTitle || card.title?.trim() || "Design Board";
    const tags = getDesignBoardTags({
      type: card.designBoardType as TaggableDesignBoard["type"],
      roomType: card.roomType as TaggableDesignBoard["roomType"],
      pattern: card.pattern as TaggableDesignBoard["pattern"],
      texture: card.texture as TaggableDesignBoard["texture"],
    });

    return {
      id: card.id || `design-board-${fileId}-${card.image?._id ?? imageUrl}`,
      fileId,
      fileType: "design-board",
      imageId,
      imageUrl,
      altText: card.image?.title ?? title,
      title,
      tags,
      designerId: String(card.designerId ?? ""),
      sortKey:
        typeof card.displayOrder === "number"
          ? card.displayOrder
          : getSortKeyFromObjectId(imageId || fileId),
      designBoardType: card.designBoardType ?? null,
      roomType: (card.roomType ?? []).filter(Boolean).map((value) => String(value)),
      pattern: (card.pattern ?? []).filter(Boolean).map((value) => String(value)),
      texture: (card.texture ?? []).filter(Boolean).map((value) => String(value)),
      color: (card.color ?? []).filter(Boolean).map((value) => String(value)),
      finish: (card.finish ?? []).filter(Boolean).map((value) => String(value)),
    } as PublicDesignerCard;
  }, []);

  const fetchProjectCards = useCallback(
    async (offset: number, limit: number) => {
      const result = await getProjectFileCards(
        offset,
        limit,
        designerIdFilter || undefined
      );
      return result
        .map((card) => mapProjectCard(card))
        .filter(Boolean) as PublicDesignerCard[];
    },
    [designerIdFilter, getProjectFileCards, mapProjectCard]
  );

  const fetchBeforeAfterCards = useCallback(
    async (offset: number, limit: number) => {
      const result = await getBeforeAfterFileCards(
        offset,
        limit,
        designerIdFilter || undefined
      );
      return result
        .map((card) => mapBeforeAfterCard(card))
        .filter(Boolean) as PublicDesignerCard[];
    },
    [designerIdFilter, getBeforeAfterFileCards, mapBeforeAfterCard]
  );

  const fetchDesignBoardCards = useCallback(
    async (offset: number, limit: number) => {
      const result = await getDesignBoardFileCards(
        offset,
        limit,
        designerIdFilter || undefined
      );
      return result
        .map((card) => mapDesignBoardCard(card))
        .filter(Boolean) as PublicDesignerCard[];
    },
    [designerIdFilter, getDesignBoardFileCards, mapDesignBoardCard]
  );

  const getFetchTypes = useCallback((): DesignerFileType[] => {
    if (!hasFullAccess) return ["project"];
    if (activeFilter === "project") return ["project"];
    if (activeFilter === "before-after") return ["before-after"];
    if (activeFilter === "design-board" || activeFilter === "ideas")
      return ["design-board"];
    return ["project", "before-after", "design-board"];
  }, [activeFilter, hasFullAccess]);

  const getPerTypeLimits = (types: DesignerFileType[]) => {
    if (types.length === 0) return [];
    const base = Math.floor(PAGE_SIZE / types.length);
    const remainder = PAGE_SIZE % types.length;
    return types.map((_, index) => base + (index < remainder ? 1 : 0));
  };

  const loadMoreCards = useCallback(
    async (types: DesignerFileType[], showLoading: boolean = false) => {
      if (isBatchLoadingRef.current) return;
      if (!types.length) return;

      isBatchLoadingRef.current = true;
      if (showLoading) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
        isLoadingMoreRef.current = true;
      }

      try {
        const limits = getPerTypeLimits(types);
        const results = await Promise.all(
          types.map(async (type, index) => {
            const limit = limits[index] ?? 0;
            if (limit <= 0) {
              return { type, cards: [] as PublicDesignerCard[], limit: 0 };
            }

            if (type === "project") {
              if (!hasMoreProjectCards && projectCardsRef.current.length > 0) {
                return { type, cards: [] as PublicDesignerCard[], limit: 0 };
              }
              const offset = projectCardsRef.current.length;
              let effectiveLimit = limit;
              if (!hasFullAccess) {
                const remaining = maxCards - offset;
                if (remaining <= 0) {
                  setHasMoreProjectCards(false);
                  return { type, cards: [] as PublicDesignerCard[], limit: 0 };
                }
                effectiveLimit = Math.min(effectiveLimit, remaining);
              }
              const cards = await fetchProjectCards(offset, effectiveLimit);
              return { type, cards, limit: effectiveLimit };
            }

            if (type === "before-after") {
              if (!hasFullAccess) {
                return { type, cards: [] as PublicDesignerCard[], limit: 0 };
              }
              if (
                !hasMoreBeforeAfterCards &&
                beforeAfterCardsRef.current.length > 0
              ) {
                return { type, cards: [] as PublicDesignerCard[], limit: 0 };
              }
              const offset = beforeAfterCardsRef.current.length;
              const cards = await fetchBeforeAfterCards(offset, limit);
              return { type, cards, limit };
            }

            if (type === "design-board") {
              if (!hasFullAccess) {
                return { type, cards: [] as PublicDesignerCard[], limit: 0 };
              }
              if (
                !hasMoreDesignBoardCards &&
                designBoardCardsRef.current.length > 0
              ) {
                return { type, cards: [] as PublicDesignerCard[], limit: 0 };
              }
              const offset = designBoardCardsRef.current.length;
              const cards = await fetchDesignBoardCards(offset, limit);
              return { type, cards, limit };
            }

            return { type, cards: [] as PublicDesignerCard[], limit: 0 };
          })
        );

        results.forEach(({ type, cards, limit }) => {
          if (type === "project") {
            if (cards.length) {
              setProjectCards((prev) => appendCards(prev, cards));
            }
            const nextCount = projectCardsRef.current.length + cards.length;
            if (!hasFullAccess && nextCount >= maxCards) {
              setHasMoreProjectCards(false);
            } else if (limit > 0 && cards.length < limit) {
              setHasMoreProjectCards(false);
            }
          }

          if (type === "before-after") {
            if (cards.length) {
              setBeforeAfterCards((prev) => appendCards(prev, cards));
            }
            if (limit > 0 && cards.length < limit) {
              setHasMoreBeforeAfterCards(false);
            }
          }

          if (type === "design-board") {
            if (cards.length) {
              setDesignBoardCards((prev) => appendCards(prev, cards));
            }
            if (limit > 0 && cards.length < limit) {
              setHasMoreDesignBoardCards(false);
            }
          }
        });
      } finally {
        if (showLoading) {
          setIsLoading(false);
        } else {
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        }
        isBatchLoadingRef.current = false;
      }
    },
    [
      fetchBeforeAfterCards,
      fetchDesignBoardCards,
      fetchProjectCards,
      appendCards,
      hasFullAccess,
      hasMoreBeforeAfterCards,
      hasMoreDesignBoardCards,
      hasMoreProjectCards,
      maxCards,
    ]
  );

  useEffect(() => {
    const fetchKey = `${hasFullAccess ? "full" : "preview"}:${user?.id ?? "anon"}:${designerIdFilter || "all-designers"}`;
    if (fetchKeyRef.current === fetchKey) return;
    fetchKeyRef.current = fetchKey;

    setProjectCards([]);
    setBeforeAfterCards([]);
    setDesignBoardCards([]);
    setAllCardsState([]);
    projectCardsRef.current = [];
    beforeAfterCardsRef.current = [];
    designBoardCardsRef.current = [];
    allCardsRef.current = [];
    shuffleStepRef.current = 0;
    setHasMoreProjectCards(true);
    setHasMoreBeforeAfterCards(hasFullAccess);
    setHasMoreDesignBoardCards(hasFullAccess);
    setVisibleCount(Math.min(PAGE_SIZE, maxCards));

    const initialTypes = hasFullAccess
      ? (["project", "before-after", "design-board"] as DesignerFileType[])
      : (["project"] as DesignerFileType[]);
    loadMoreCards(initialTypes, true);
  }, [designerIdFilter, hasFullAccess, loadMoreCards, maxCards, user?.id]);

  const projectTypeOptions = useMemo(
    () => sortOptionsByLabel(projectCategoryOptionsList),
    []
  );
  const projectStyleFilterOptions = useMemo(
    () => sortOptionsByLabel(projectStyleOptions),
    []
  );
  const budgetRangeFilterOptions = useMemo(
    () => sortOptionsByLabel(budgetRangeOptions),
    []
  );
  const squareFootageFilterOptions = useMemo(
    () => sortOptionsByLabel(squareFootageOptions),
    []
  );
  const specialRequirementsFilterOptions = useMemo(
    () => sortOptionsByLabel(specialRequirementsOptions),
    []
  );
  const colorFilterOptions = useMemo(
    () => sortOptionsByLabel(colorFamilyOptions),
    []
  );
  const textureFilterOptions = useMemo(
    () => sortOptionsByLabel(textureOptions),
    []
  );
  const patternFilterOptions = useMemo(
    () => sortOptionsByLabel(patternOptions),
    []
  );
  const finishFilterOptions = useMemo(
    () => sortOptionsByLabel(finishOptions),
    []
  );

  const filterByDesigner = useCallback(
    (cards: PublicDesignerCard[]) =>
      designerIdFilter
        ? cards.filter((card) => card.designerId === designerIdFilter)
        : cards,
    [designerIdFilter]
  );

  const matchesProjectFilters = useCallback(
    (card: PublicDesignerCard, filters: ProjectFilterState) => {
      if (filters.projectTypes.length) {
        if (!card.projectCategory || !filters.projectTypes.includes(card.projectCategory)) {
          return false;
        }
      }
      if (filters.projectStyles.length) {
        const styleValue = card.projectStyle ?? "";
        if (!styleValue || !filters.projectStyles.includes(styleValue)) {
          return false;
        }
      }
      if (filters.budgetRanges.length) {
        if (!card.budgetRange || !filters.budgetRanges.includes(card.budgetRange)) {
          return false;
        }
      }
      if (filters.squareFootages.length) {
        if (!card.squareFootage || !filters.squareFootages.includes(card.squareFootage)) {
          return false;
        }
      }
      if (filters.specialRequirements.length) {
        const requirements = card.specialRequirements ?? [];
        if (!filters.specialRequirements.some((value) => requirements.includes(value))) {
          return false;
        }
      }
      return true;
    },
    []
  );

  const matchesDesignBoardFilters = useCallback(
    (card: PublicDesignerCard, filters: DesignBoardFilterState) => {
      if (filters.activeType && card.designBoardType !== filters.activeType) {
        return false;
      }
      if (!filters.activeType) return true;

      const hasAny = (values: string[] | null | undefined, selected: string[]) => {
        if (!selected.length) return true;
        const list = values ?? [];
        return selected.some((value) => list.includes(value));
      };

      switch (filters.activeType) {
        case "Mood":
          return (
            hasAny(card.roomType, filters.mood.projectTypes) &&
            hasAny(card.color, filters.mood.colors)
          );
        case "Color":
          return hasAny(card.color, filters.color.colors);
        case "TextureAndFinish":
          return (
            hasAny(card.texture, filters.texture.textures) &&
            hasAny(card.finish, filters.texture.finishes)
          );
        case "PatternPlay":
          return (
            hasAny(card.pattern, filters.pattern.patterns) &&
            hasAny(card.color, filters.pattern.colors)
          );
        case "StyleFusion":
          return (
            hasAny(card.pattern, filters.styleFusion.patterns) &&
            hasAny(card.color, filters.styleFusion.colors)
          );
        default:
          return true;
      }
    },
    []
  );

  const projectCardsFiltered = useMemo(() => {
    const list = filterByDesigner(projectCards).filter((card) =>
      matchesProjectFilters(card, projectFilters)
    );
    return list;
  }, [filterByDesigner, matchesProjectFilters, projectCards, projectFilters]);

  const beforeAfterCardsFiltered = useMemo(() => {
    const list = filterByDesigner(beforeAfterCards).filter((card) =>
      matchesProjectFilters(card, beforeAfterFilters)
    );
    return list;
  }, [beforeAfterCards, beforeAfterFilters, filterByDesigner, matchesProjectFilters]);

  const designBoardCardsFiltered = useMemo(() => {
    const list = filterByDesigner(designBoardCards).filter((card) =>
      matchesDesignBoardFilters(card, designBoardFilters)
    );
    return list;
  }, [designBoardCards, designBoardFilters, filterByDesigner, matchesDesignBoardFilters]);

  const projectFilterSelectionCount =
    projectFilters.projectTypes.length +
    projectFilters.projectStyles.length +
    projectFilters.budgetRanges.length +
    projectFilters.squareFootages.length +
    projectFilters.specialRequirements.length;
  const beforeAfterFilterSelectionCount =
    beforeAfterFilters.projectTypes.length +
    beforeAfterFilters.projectStyles.length +
    beforeAfterFilters.budgetRanges.length +
    beforeAfterFilters.squareFootages.length +
    beforeAfterFilters.specialRequirements.length;
  const designBoardFilterSelectionTotal =
    designBoardFilters.mood.projectTypes.length +
    designBoardFilters.mood.projectStyles.length +
    designBoardFilters.mood.colors.length +
    designBoardFilters.color.colors.length +
    designBoardFilters.texture.textures.length +
    designBoardFilters.texture.finishes.length +
    designBoardFilters.pattern.patterns.length +
    designBoardFilters.pattern.colors.length +
    designBoardFilters.styleFusion.patterns.length +
    designBoardFilters.styleFusion.colors.length;
  const allCards = useMemo(() => {
    return allCardsState;
  }, [allCardsState]);

  const filteredCards = useMemo(() => {
    if (activeFilter === "all") {
      if (designerIdFilter) {
        return [
          ...projectCardsFiltered,
          ...beforeAfterCardsFiltered,
          ...designBoardCardsFiltered,
        ];
      }
      return filterByDesigner(allCards);
    }
    if (activeFilter === "project") {
      return projectCardsFiltered;
    }
    if (activeFilter === "before-after") {
      return beforeAfterCardsFiltered;
    }
    if (activeFilter === "design-board" || activeFilter === "ideas") {
      return designBoardCardsFiltered;
    }
    return [...projectCardsFiltered, ...beforeAfterCardsFiltered, ...designBoardCardsFiltered];
  }, [
    activeFilter,
    allCards,
    beforeAfterCardsFiltered,
    designerIdFilter,
    designBoardCardsFiltered,
    filterByDesigner,
    projectCardsFiltered,
  ]);

  const projectFilterKey = JSON.stringify(projectFilters);
  const beforeAfterFilterKey = JSON.stringify(beforeAfterFilters);
  const designBoardFilterKey = JSON.stringify(designBoardFilters);

  useEffect(() => {
    setVisibleCount(Math.min(PAGE_SIZE, maxCards));
  }, [activeFilter, projectFilterKey, beforeAfterFilterKey, designBoardFilterKey, maxCards]);

  const visibleCards = useMemo(
    () =>
      filteredCards
        .slice(0, visibleCount)
        .filter((card): card is PublicDesignerCard => Boolean(card)),
    [filteredCards, visibleCount]
  );
  const showInitialSkeleton =
    isLoading ||
    (trackImages &&
      !imagesReady &&
      (!hasLoadedOnce || filteredCards.length === 0));
  const showLoadMoreSkeleton =
    hasLoadedOnce && (isLoadingMore || (trackImages && !imagesReady));
  const skeletonHeights = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, index) => {
        const heights = [220, 280, 240, 300, 260, 210, 290, 230, 270, 250];
        return heights[index % heights.length];
      }),
    []
  );
  const skeletonItems = useMemo(
    () =>
      skeletonHeights.map((height, index) => ({
        id: `designer-files-skeleton-${index}`,
        kind: "skeleton" as const,
        height,
      })),
    [skeletonHeights]
  );
  const loadMoreSkeletonHeights = useMemo(
    () =>
      Array.from({ length: 6 }).map((_, index) => {
        const heights = [220, 280, 240, 300, 260, 210];
        return heights[index % heights.length];
      }),
    []
  );
  const loadMoreSkeletonItems = useMemo(
    () =>
      loadMoreSkeletonHeights.map((height, index) => ({
        id: `designer-files-load-more-skeleton-${index}`,
        kind: "skeleton" as const,
        height,
      })),
    [loadMoreSkeletonHeights]
  );
  const masonryItems = useMemo(() => {
    const items = visibleCards.map((card) => ({
      id: card.id,
      kind: "card" as const,
      card,
    }));
    if (showLoadMoreSkeleton) {
      return [...items, ...loadMoreSkeletonItems];
    }
    return items;
  }, [loadMoreSkeletonItems, showLoadMoreSkeleton, visibleCards]);
  const masonryColumnSx = {
    width: "100%",
    "& .designer-files-masonry": {
      display: "flex",
      marginLeft: `-${MASONRY_GUTTER}px`,
      width: "auto",
    },
    "& .designer-files-masonry-column": {
      display: "flex",
      flexDirection: "column",
      paddingLeft: `${MASONRY_GUTTER}px`,
      gap: `${MASONRY_GUTTER}px`,
    },
  };
  const masonryColumns = useMemo(() => {
    if (isMobile) return 2;
    const paddingEstimate = 64;
    const available = Math.max(0, viewportWidth - paddingEstimate);
    const columnWidth = MASONRY_CARD_MIN_WIDTH + MASONRY_GUTTER;
    return Math.max(1, Math.floor((available + MASONRY_GUTTER) / columnWidth));
  }, [isMobile, viewportWidth]);
  const safeSkeletonItems = useMemo(
    () =>
      skeletonItems.filter(
        (item): item is typeof skeletonItems[number] =>
          Boolean(item) && typeof item === "object"
      ),
    [skeletonItems]
  );
  const safeMasonryItems = useMemo(
    () =>
      masonryItems.filter(
        (item): item is typeof masonryItems[number] =>
          Boolean(item) && typeof item === "object"
      ),
    [masonryItems]
  );

  useEffect(() => {
    if (!trackImages || isLoading) return;
    if (isLoadingMore && visibleCards.length < visibleCount) return;
    const loadedIds = new Set<string>();
    visibleCards.forEach((card) => {
      if (!card.imageUrl) return;
      if (loadedImageIdsRef.current.has(card.id)) {
        loadedIds.add(card.id);
      }
    });
    imageLoadSetRef.current = loadedIds;
    imageTotalRef.current = visibleCards.filter((card) => card.imageUrl).length;
    if (
      imageTotalRef.current === 0 ||
      loadedIds.size >= imageTotalRef.current
    ) {
      setImagesReady(true);
      setTrackImages(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [trackImages, isLoading, isLoadingMore, visibleCards, visibleCount]);

  useEffect(() => {
    if (!trackImages || isLoading || imagesReady) return;
    const timer = window.setTimeout(() => {
      setImagesReady(true);
      setTrackImages(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }, IMAGE_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [imagesReady, isLoading, trackImages]);

  const handleCardImageLoad = useCallback(
    (cardId: string) => {
      loadedImageIdsRef.current.add(cardId);
      if (!trackImages) return;
      if (imageLoadSetRef.current.has(cardId)) return;
      imageLoadSetRef.current.add(cardId);
      if (imageLoadSetRef.current.size >= imageTotalRef.current) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setImagesReady(true);
            setTrackImages(false);
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
          });
        });
      }
    },
    [trackImages]
  );

  useEffect(() => {
    if (hasLoadedOnce) return;
    if (!imagesReady) return;
    if (visibleCards.length === 0) return;
    setHasLoadedOnce(true);
  }, [hasLoadedOnce, imagesReady, visibleCards.length]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);
  const hasMoreForActiveFilter = useMemo(() => {
    if (!hasFullAccess) return hasMoreProjectCards;
    if (activeFilter === "project") return hasMoreProjectCards;
    if (activeFilter === "before-after") return hasMoreBeforeAfterCards;
    if (activeFilter === "design-board" || activeFilter === "ideas")
      return hasMoreDesignBoardCards;
    return (
      hasMoreProjectCards || hasMoreBeforeAfterCards || hasMoreDesignBoardCards
    );
  }, [
    activeFilter,
    hasFullAccess,
    hasMoreBeforeAfterCards,
    hasMoreDesignBoardCards,
    hasMoreProjectCards,
  ]);
  const canLoadMore = visibleCount < filteredCards.length || hasMoreForActiveFilter;

  const handleRequestAppend = useCallback(() => {
    if (!canLoadMore) return;
    if (isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    setTrackImages(true);
    setImagesReady(false);
    imageLoadSetRef.current = new Set();
    imageTotalRef.current = 0;
    const lastVisible = visibleCards[visibleCards.length - 1];
    if (lastVisible) {
      const el = document.querySelector<HTMLElement>(
        `[data-masonry-id="${lastVisible.id}"]`
      );
      if (el) {
        scrollAnchorRef.current = {
          id: lastVisible.id,
          top: el.getBoundingClientRect().top,
        };
      }
    }
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, maxCards));
  }, [canLoadMore, maxCards, visibleCards]);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const scrollAnchorRef = useRef<{ id: string; top: number } | null>(null);

  useEffect(() => {
    if (!canLoadMore) return;
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleRequestAppend();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [canLoadMore, handleRequestAppend]);

  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (!anchor) return;
    const el = document.querySelector<HTMLElement>(
      `[data-masonry-id="${anchor.id}"]`
    );
    if (!el) return;
    const applyAnchor = () => {
      const nextTop = el.getBoundingClientRect().top;
      const delta = nextTop - anchor.top;
      if (delta !== 0) {
        window.scrollBy({ top: delta, left: 0 });
      }
    };
    applyAnchor();
    requestAnimationFrame(applyAnchor);
    setTimeout(applyAnchor, 50);
    setTimeout(() => {
      applyAnchor();
      scrollAnchorRef.current = null;
    }, 150);
  }, [visibleCount]);

  useEffect(() => {
    if (!canLoadMore) return;
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (!canLoadMore) return;
        const scrollBottom = window.innerHeight + window.scrollY;
        const threshold = document.body.offsetHeight - 800;
        if (scrollBottom >= threshold) {
          handleRequestAppend();
        }
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [canLoadMore, handleRequestAppend]);

  useEffect(() => {
    if (!hasMoreForActiveFilter) return;
    if (visibleCount <= filteredCards.length) return;
    if (!isLoadingMore) {
      setTrackImages(true);
      setImagesReady(false);
      imageLoadSetRef.current = new Set();
      imageTotalRef.current = 0;
    }
    loadMoreCards(getFetchTypes(), false);
  }, [
    visibleCount,
    filteredCards.length,
    getFetchTypes,
    hasMoreForActiveFilter,
    loadMoreCards,
    isLoadingMore,
  ]);

  const resolveDesignBoardType = (view: DrawerView) => {
    switch (view) {
      case "design-board-mood":
        return "Mood";
      case "design-board-color":
        return "Color";
      case "design-board-pattern":
        return "PatternPlay";
      case "design-board-style-fusion":
        return "StyleFusion";
      case "design-board-texture":
        return "TextureAndFinish";
      default:
        return null;
    }
  };

  const resolveDefaultSection = (view: DrawerView) => {
    switch (view) {
      case "before-after":
      case "project":
      case "design-board-mood":
        return "projectType";
      case "design-board-color":
        return "color";
      case "design-board-texture":
        return "texture";
      case "design-board-pattern":
      case "design-board-style-fusion":
        return "pattern";
      default:
        return "";
    }
  };

  const resolveFilterKeyFromDrawerView = (view: DrawerView): FilterKey => {
    if (view === "project") return "project";
    if (view === "before-after") return "before-after";
    if (view === "design-boards") return "design-board";
    if (view.startsWith("design-board")) return "design-board";
    return "all";
  };

  const openDrawer = (view: DrawerView) => {
    const nextProjectDraft = cloneProjectFilters(projectFilters);
    const nextBeforeAfterDraft = cloneProjectFilters(beforeAfterFilters);
    const nextDesignDraft = cloneDesignBoardFilters(designBoardFilters);
    const boardType = resolveDesignBoardType(view);
    if (boardType) {
      nextDesignDraft.activeType = boardType;
    }
    setDraftProjectFilters(nextProjectDraft);
    setDraftBeforeAfterFilters(nextBeforeAfterDraft);
    setDraftDesignBoardFilters(nextDesignDraft);
    setDrawerView(view);
    setExpandedSection(resolveDefaultSection(view));
    setDrawerOpen(true);
  };

  const navigateDrawer = (view: DrawerView) => {
    const boardType = resolveDesignBoardType(view);
    if (boardType) {
      setDraftDesignBoardFilters((prev) => ({
        ...prev,
        activeType: boardType,
      }));
    }
    setDrawerView(view);
    setExpandedSection(resolveDefaultSection(view));
  };

  const handleApplyFilters = () => {
    setProjectFilters(cloneProjectFilters(draftProjectFilters));
    setBeforeAfterFilters(cloneProjectFilters(draftBeforeAfterFilters));
    setDesignBoardFilters(cloneDesignBoardFilters(draftDesignBoardFilters));
    setDrawerOpen(false);
  };

  const resetAllFilters = () => {
    const resetProject = cloneProjectFilters(defaultProjectFilters);
    const resetBefore = cloneProjectFilters(defaultProjectFilters);
    const resetDesign = cloneDesignBoardFilters(defaultDesignBoardFilters);
    setProjectFilters(resetProject);
    setBeforeAfterFilters(resetBefore);
    setDesignBoardFilters(resetDesign);
    setDraftProjectFilters(resetProject);
    setDraftBeforeAfterFilters(resetBefore);
    setDraftDesignBoardFilters(resetDesign);
  };

  const handleClearFilters = () => {
    resetAllFilters();
    setActiveFilter("all");
  };

  useEffect(() => {
    if (activeFilter !== "all") return;
    resetAllFilters();
  }, [activeFilter]);

  const handleDrawerClose = () => setDrawerOpen(false);
  const handleDrawerBack = () => {
    if (
      drawerView === "design-board-mood" ||
      drawerView === "design-board-color" ||
      drawerView === "design-board-texture" ||
      drawerView === "design-board-pattern" ||
      drawerView === "design-board-style-fusion"
    ) {
      navigateDrawer("design-boards");
      return;
    }
    navigateDrawer("root");
  };

  const toggleProjectDraftValue = (
    field: keyof ProjectFilterState,
    value: string,
    target: "project" | "before-after"
  ) => {
    const setter =
      target === "project" ? setDraftProjectFilters : setDraftBeforeAfterFilters;
    setter((prev) => ({
      ...prev,
      [field]: toggleValue(prev[field], value),
    }));
  };

  const toggleDesignBoardDraftValue = (
    section: keyof DesignBoardFilterState,
    field: string,
    value: string
  ) => {
    if (section === "activeType") return;
    setDraftDesignBoardFilters((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] as Record<string, string[]>),
        [field]: toggleValue((prev[section] as Record<string, string[]>)[field], value),
      },
    }));
  };

  const handleSectionToggle = (section: string) => {
    setExpandedSection((prev) => (prev === section ? "" : section));
  };

  const renderSection = (
    label: string,
    sectionKey: string,
    options: { value: string; label: string }[],
    selectedValues: string[],
    onToggle: (value: string) => void
  ) => (
    <Box key={sectionKey}>
      <Box
        onClick={() => handleSectionToggle(sectionKey)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          cursor: "pointer",
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
        {expandedSection === sectionKey ? (
          <KeyboardArrowUpIcon />
        ) : (
          <KeyboardArrowDownIcon />
        )}
      </Box>
      <Collapse in={expandedSection === sectionKey} timeout="auto" unmountOnExit>
        <Stack spacing={1.5} sx={{ pb: 2 }}>
          {options.map((option) => (
            <Box
              key={option.value}
              sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
            >
              <Checkbox
                checked={selectedValues.includes(option.value)}
                onChange={() => onToggle(option.value)}
                sx={{
                  p: 0,
                  color: "#D1D5DB",
                  "&.Mui-checked": { color: "#111827" },
                }}
              />
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
      <Divider />
    </Box>
  );

  const renderNavItem = (label: string, onClick: () => void) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        cursor: "pointer",
      }}
    >
      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
      <ChevronRightIcon />
    </Box>
  );

  const selectedFilters = useMemo(() => {
    const selections: SelectedFilter[] = [];
    projectFilters.projectTypes.forEach((value) => {
      const label = formatProjectCategoryLabel(value);
      if (!label) return;
      selections.push({ type: "project", value, label });
    });
    beforeAfterFilters.projectTypes.forEach((value) => {
      const label = formatProjectCategoryLabel(value);
      if (!label) return;
      selections.push({ type: "before-after", value, label });
    });
    return selections;
  }, [beforeAfterFilters.projectTypes, projectFilters.projectTypes]);

  const handleRemoveFilter = (filter: SelectedFilter) => {
    if (filter.type === "project") {
      setProjectFilters((prev) => ({
        ...prev,
        projectTypes: prev.projectTypes.filter((item) => item !== filter.value),
      }));
      return;
    }
    setBeforeAfterFilters((prev) => ({
      ...prev,
      projectTypes: prev.projectTypes.filter((item) => item !== filter.value),
    }));
  };

  const filterButtonSx = (isActive: boolean) => ({
    borderRadius: "8px",
    textTransform: "none",
    border: "1px solid",
    borderColor: isActive ? "#111827" : "#D0D5DD",
    color: "#111827",
    backgroundColor: "#fff",
    fontWeight: 500,
    fontSize: "14px",
    px: 2,
    flexShrink: 0,
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#111827",
      backgroundColor: "#F3F4F6",
    },
  });

  const getSelectionLabel = (label: string, count: number) => {
    if (!count) return label;
    return `${label} (${count})`;
  };

  const designBoardFilterSelectionCount = useMemo(() => {
    switch (designBoardFilters.activeType) {
      case "Mood":
        return (
          designBoardFilters.mood.projectTypes.length +
          designBoardFilters.mood.projectStyles.length +
          designBoardFilters.mood.colors.length
        );
      case "Color":
        return designBoardFilters.color.colors.length;
      case "TextureAndFinish":
        return (
          designBoardFilters.texture.textures.length +
          designBoardFilters.texture.finishes.length
        );
      case "PatternPlay":
        return (
          designBoardFilters.pattern.patterns.length +
          designBoardFilters.pattern.colors.length
        );
      case "StyleFusion":
        return (
          designBoardFilters.styleFusion.patterns.length +
          designBoardFilters.styleFusion.colors.length
        );
      default:
        return 0;
    }
  }, [designBoardFilters]);

  const projectButtonLabel = getSelectionLabel(
    FILTER_LABELS.project,
    projectFilterSelectionCount
  );
  const beforeAfterButtonLabel = getSelectionLabel(
    FILTER_LABELS["before-after"],
    beforeAfterFilterSelectionCount
  );
  const designBoardButtonLabel = getSelectionLabel(
    FILTER_LABELS["design-board"],
    designBoardFilterSelectionCount
  );

  const resultsLabel =
    activeFilter === "project"
      ? projectButtonLabel
      : activeFilter === "before-after"
      ? beforeAfterButtonLabel
      : activeFilter === "design-board"
      ? designBoardButtonLabel
      : FILTER_LABELS[activeFilter];

  const drawerTitle = useMemo(() => {
    if (drawerView === "project" || drawerView === "before-after" || drawerView === "design-boards") {
      return "Filter By:";
    }
    switch (drawerView) {
      case "root":
        return "Filter & Sort";
      case "design-board-mood":
        return "Mood Board";
      case "design-board-color":
        return "Color Board";
      case "design-board-texture":
        return "Texture & Finishes";
      case "design-board-pattern":
        return "Pattern Play";
      case "design-board-style-fusion":
        return "Style Fusion";
      default:
        return "Filter";
    }
  }, [drawerView]);

  const drawerSubtitle = useMemo(() => {
    switch (drawerView) {
      case "project":
        return "Projects";
      case "before-after":
        return "Before & Afters";
      case "design-boards":
        return "Design Boards";
      default:
        return "";
    }
  }, [drawerView]);

  const isDrawerListView = drawerView === "root" || drawerView === "design-boards";
  const showDrawerBack =
    drawerView === "design-board-mood" ||
    drawerView === "design-board-color" ||
    drawerView === "design-board-texture" ||
    drawerView === "design-board-pattern" ||
    drawerView === "design-board-style-fusion";
  const showDrawerActions = !isDrawerListView;

  const drawerBody = (
    () => {
      switch (drawerView) {
        case "root":
          return (
            <Box>
              {renderNavItem("Before & Afters", () => navigateDrawer("before-after"))}
              <Divider />
              {renderNavItem("Project Type:", () => navigateDrawer("project"))}
              <Divider />
              {renderNavItem("Design Boards", () => navigateDrawer("design-boards"))}
              <Divider />
            </Box>
          );
        case "design-boards":
          return (
            <Box>
              {renderNavItem("Mood Board", () => navigateDrawer("design-board-mood"))}
              <Divider />
              {renderNavItem("Color Board", () => navigateDrawer("design-board-color"))}
              <Divider />
              {renderNavItem("Texture & Finishes", () =>
                navigateDrawer("design-board-texture")
              )}
              <Divider />
              {renderNavItem("Pattern Play", () => navigateDrawer("design-board-pattern"))}
              <Divider />
              {renderNavItem("Style Fusion", () =>
                navigateDrawer("design-board-style-fusion")
              )}
              <Divider />
            </Box>
          );
        case "project":
          return (
            <Box>
              {renderSection(
                "Project Type",
                "projectType",
                projectTypeOptions,
                draftProjectFilters.projectTypes,
                (value) => toggleProjectDraftValue("projectTypes", value, "project")
              )}
              {renderSection(
                "Project Style",
                "projectStyle",
                projectStyleFilterOptions,
                draftProjectFilters.projectStyles,
                (value) => toggleProjectDraftValue("projectStyles", value, "project")
              )}
              {renderSection(
                "Budget Range",
                "budgetRange",
                budgetRangeFilterOptions,
                draftProjectFilters.budgetRanges,
                (value) => toggleProjectDraftValue("budgetRanges", value, "project")
              )}
              {renderSection(
                "Square Footage",
                "squareFootage",
                squareFootageFilterOptions,
                draftProjectFilters.squareFootages,
                (value) => toggleProjectDraftValue("squareFootages", value, "project")
              )}
              {renderSection(
                "Special Requirements",
                "specialRequirements",
                specialRequirementsFilterOptions,
                draftProjectFilters.specialRequirements,
                (value) =>
                  toggleProjectDraftValue("specialRequirements", value, "project")
              )}
            </Box>
          );
        case "before-after":
          return (
            <Box>
              {renderSection(
                "Project Type",
                "projectType",
                projectTypeOptions,
                draftBeforeAfterFilters.projectTypes,
                (value) => toggleProjectDraftValue("projectTypes", value, "before-after")
              )}
              {renderSection(
                "Project Style",
                "projectStyle",
                projectStyleFilterOptions,
                draftBeforeAfterFilters.projectStyles,
                (value) =>
                  toggleProjectDraftValue("projectStyles", value, "before-after")
              )}
              {renderSection(
                "Budget Range",
                "budgetRange",
                budgetRangeFilterOptions,
                draftBeforeAfterFilters.budgetRanges,
                (value) =>
                  toggleProjectDraftValue("budgetRanges", value, "before-after")
              )}
              {renderSection(
                "Square Footage",
                "squareFootage",
                squareFootageFilterOptions,
                draftBeforeAfterFilters.squareFootages,
                (value) =>
                  toggleProjectDraftValue("squareFootages", value, "before-after")
              )}
              {renderSection(
                "Special Requirements",
                "specialRequirements",
                specialRequirementsFilterOptions,
                draftBeforeAfterFilters.specialRequirements,
                (value) =>
                  toggleProjectDraftValue(
                    "specialRequirements",
                    value,
                    "before-after"
                  )
              )}
            </Box>
          );
        case "design-board-mood":
          return (
            <Box>
              {renderSection(
                "Project Type",
                "projectType",
                projectTypeOptions,
                draftDesignBoardFilters.mood.projectTypes,
                (value) => toggleDesignBoardDraftValue("mood", "projectTypes", value)
              )}
              {renderSection(
                "Project Style",
                "projectStyle",
                projectStyleFilterOptions,
                draftDesignBoardFilters.mood.projectStyles,
                (value) => toggleDesignBoardDraftValue("mood", "projectStyles", value)
              )}
              {renderSection(
                "Color",
                "color",
                colorFilterOptions,
                draftDesignBoardFilters.mood.colors,
                (value) => toggleDesignBoardDraftValue("mood", "colors", value)
              )}
            </Box>
          );
        case "design-board-color":
          return (
            <Box>
              {renderSection(
                "Color",
                "color",
                colorFilterOptions,
                draftDesignBoardFilters.color.colors,
                (value) => toggleDesignBoardDraftValue("color", "colors", value)
              )}
            </Box>
          );
        case "design-board-texture":
          return (
            <Box>
              {renderSection(
                "Texture",
                "texture",
                textureFilterOptions,
                draftDesignBoardFilters.texture.textures,
                (value) => toggleDesignBoardDraftValue("texture", "textures", value)
              )}
              {renderSection(
                "Finishes",
                "finishes",
                finishFilterOptions,
                draftDesignBoardFilters.texture.finishes,
                (value) => toggleDesignBoardDraftValue("texture", "finishes", value)
              )}
            </Box>
          );
        case "design-board-pattern":
          return (
            <Box>
              {renderSection(
                "Pattern",
                "pattern",
                patternFilterOptions,
                draftDesignBoardFilters.pattern.patterns,
                (value) => toggleDesignBoardDraftValue("pattern", "patterns", value)
              )}
              {renderSection(
                "Color",
                "color",
                colorFilterOptions,
                draftDesignBoardFilters.pattern.colors,
                (value) => toggleDesignBoardDraftValue("pattern", "colors", value)
              )}
            </Box>
          );
        case "design-board-style-fusion":
          return (
            <Box>
              {renderSection(
                "Pattern",
                "pattern",
                patternFilterOptions,
                draftDesignBoardFilters.styleFusion.patterns,
                (value) => toggleDesignBoardDraftValue("styleFusion", "patterns", value)
              )}
              {renderSection(
                "Color",
                "color",
                colorFilterOptions,
                draftDesignBoardFilters.styleFusion.colors,
                (value) => toggleDesignBoardDraftValue("styleFusion", "colors", value)
              )}
            </Box>
          );
        default:
          return null;
      }
    }
  )();
  const showAnySkeleton = showInitialSkeleton || showLoadMoreSkeleton;
  const showJoinCta = !hasFullAccess;
  const showSignIn = !user;
  const filtersDisabled = !user;
  const previewDialogType =
    previewFileType === "design-board" ? "mood-board" : previewFileType;
  const backToPath = `${location.pathname}${location.search}`;

  const handleOpenPreview = (fileType: DesignerFileType, fileId: string) => {
    if (isMobile && fileType === "project" && designerIdFilter) {
      navigate(
        Pages.DESIGNER_PROJECT_DETAILS.replace(":designerId", designerIdFilter).replace(
          ":projectId",
          fileId
        ),
        {
          state: {
            backTo: backToPath,
          },
        }
      );
      return;
    }

    setPreviewFileType(fileType);
    setPreviewFileId(fileId);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFileId(null);
    setPreviewFileType(null);
  };

  const handleOpenSavePopover = (
    event: MouseEvent<HTMLElement>,
    card: PublicDesignerCard
  ) => {
    event.stopPropagation();
    if (!canSaveToIdeaBooks || !card.imageId) return;
    setSaveAnchorEl(event.currentTarget);
    setSaveCard(card);
  };

  const handleCloseSavePopover = () => {
    setSaveAnchorEl(null);
    setSaveCard(null);
  };

  return (
    <Box sx={{ backgroundColor: "#F8F8F8", minHeight: "100vh" }}>
      {isHomeownerDesignerFilesView ? (
        <DashboardHeader title="Designer Files" />
      ) : (
        <Box
          sx={{
            "& .material-hero .site-wrapper": {
              maxWidth: "100%",
              width: "100%",
              boxSizing: "border-box",
              px: 0,
            },
            "& .material-hero .title": {
              left: 0,
              pl: { xs: 2, md: "100px" },
            },
          }}
        >
          <MaterialHero title="DESIGNER FILES" />
        </Box>
      )}

      <Box
        sx={
          isHomeownerDesignerFilesView
            ? undefined
            : {
                maxWidth: "100%",
                mx: "auto",
                px: { xs: 2, md: "100px" },
                py: { xs: 2, md: 4 },
              }
        }
      >
        {!isHomeownerDesignerFilesView && (
          <Box
            sx={{
              maxWidth: "100%",
              mx: 0,
              textAlign: "left",
              mb: { xs: 2, md: 3 },
              px: 0,
              pt: { xs: 6, md: "108px" },
            }}
          >
            <Typography
              sx={{
                color: "#D22268",
                fontSize: "31.25px",
                fontFamily: "Jost",
                fontWeight: 700,
                textTransform: "capitalize",
                lineHeight: "48px",
                wordWrap: "break-word",
                mb: { xs: 1.5, md: 2 },
              }}
            >
              Curated Inspiration, Grounded in Reality
            </Typography>
            <Typography
              sx={{
                color: "#374A67",
                fontSize: "18px",
                fontFamily: "Jost",
                fontWeight: 400,
                lineHeight: "32px",
                wordWrap: "break-word",
                maxWidth: "800px",
              }}
            >
              Unlock Confidence with a Designer Files membership. Access to a
              curated gallery showcasing the work of our vetted designers,
              offering not just inspiration, but also clarity. Each Project and
              Before-and-After set real-world expectations by revealing the cost,
              timeline, and project scope behind the design, so homeowners can
              plan smart from the start.
            </Typography>
          </Box>
        )}

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap={{ xs: "nowrap", md: "wrap" }}
          sx={{
            mb: { xs: 1.5, md: 2 },
            width: "100%",
            overflowX: { xs: "auto", md: "visible" },
            pb: { xs: 0.5, md: 0 },
            "&::-webkit-scrollbar": {
              height: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D1D5DB",
              borderRadius: 4,
            },
          }}
        >
          <Button
            onClick={() => {
              handleClearFilters();
              setActiveFilter("all");
            }}
            sx={filterButtonSx(activeFilter === "all")}
            disabled={filtersDisabled}
          >
            All Designer Files
          </Button>
          <Button
            onClick={() => {
              setActiveFilter("before-after");
              resetAllFilters();
              setDrawerOpen(false);
            }}
            sx={filterButtonSx(activeFilter === "before-after")}
            disabled={filtersDisabled}
          >
            {beforeAfterButtonLabel}
          </Button>
          <Button
            onClick={() => {
              setActiveFilter("project");
              resetAllFilters();
              setDrawerOpen(false);
            }}
            sx={filterButtonSx(activeFilter === "project")}
            disabled={filtersDisabled}
          >
            {projectButtonLabel}
          </Button>
          <Button
            onClick={() => {
              setActiveFilter("design-board");
              resetAllFilters();
              setDrawerOpen(false);
            }}
            sx={filterButtonSx(activeFilter === "design-board")}
            disabled={filtersDisabled}
          >
            {designBoardButtonLabel}
          </Button>
          {activeFilter !== "all" && (
            <Button
              onClick={() => {
                if (activeFilter === "project") openDrawer("project");
                else if (activeFilter === "before-after") openDrawer("before-after");
                else if (activeFilter === "design-board") openDrawer("design-boards");
              }}
              variant="outlined"
              disableElevation
              startIcon={<FilterListIcon />}
              sx={{ ...filterButtonSx(false), ml: { xs: 0, md: "auto" } }}
              disabled={filtersDisabled}
            >
              Filter By
            </Button>
          )}
        </Stack>

        {selectedFilters.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Button
              variant="text"
              onClick={handleClearFilters}
              sx={{
                textTransform: "none",
                color: "#6B7280",
                textDecoration: "underline",
                px: 0,
              }}
            >
              Clear All
            </Button>
            {selectedFilters.map((filter) => (
              <Chip
                key={`${filter.type}-${filter.value}`}
                label={filter.label}
                onDelete={() => handleRemoveFilter(filter)}
                deleteIcon={<CloseIcon />}
                sx={{ backgroundColor: "#F3F4F6" }}
              />
            ))}
          </Stack>
        )}

        {!showInitialSkeleton && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Showing {resultsLabel}, {visibleCards.length} of {filteredCards.length} Results
          </Typography>
        )}

        <Box sx={{ width: "100%", overflowAnchor: "none" }}>
          {filteredCards.length === 0 ? (
            showInitialSkeleton ? (
              <Box sx={masonryColumnSx}>
                <Masonry
                  breakpointCols={masonryColumns}
                  className="designer-files-masonry"
                  columnClassName="designer-files-masonry-column"
                >
                  {safeSkeletonItems.map((item, index) => (
                    <Box
                      key={item.id ?? `masonry-item-${index}`}
                      data-masonry-id={item.id}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        width: "100%",
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        height={item.kind === "skeleton" ? item.height : 220}
                      />
                      <Box sx={{ p: 2.5 }}>
                        <Skeleton variant="text" width="70%" height={26} />
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Skeleton variant="circular" width={28} height={28} />
                          <Skeleton variant="text" width="55%" height={20} />
                        </Stack>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1.5,
                          }}
                        >
                          <Skeleton variant="rounded" width={58} height={22} />
                          <Skeleton variant="rounded" width={48} height={22} />
                          <Skeleton variant="rounded" width={64} height={22} />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Masonry>
              </Box>
            ) : (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "16px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {allCards.length > 0
                    ? "No results match your filters"
                    : "No designer files available"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {allCards.length > 0
                    ? "Try clearing some filters to see more designer files."
                    : "Check back soon for curated designer projects."}
                </Typography>
              </Box>
            )
          ) : (
            <Box
              sx={{
                opacity: 1,
                transition: "opacity 0.2s ease",
                pointerEvents: showInitialSkeleton ? "none" : "auto",
              }}
            >
              <Box sx={masonryColumnSx}>
                <Masonry
                  breakpointCols={masonryColumns}
                  className="designer-files-masonry"
                  columnClassName="designer-files-masonry-column"
                >
                {safeMasonryItems.map((item, index) => {
                  if (item.kind === "skeleton") {
                    return (
                      <Box
                        key={item.id ?? `masonry-item-${index}`}
                        sx={{
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
                          overflow: "hidden",
                          width: "100%",
                        }}
                      >
                        <Skeleton variant="rectangular" height={item.height} />
                        <Box sx={{ p: 2.5 }}>
                          <Skeleton variant="text" width="70%" height={26} />
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mt: 1 }}
                          >
                            <Skeleton variant="circular" width={28} height={28} />
                            <Skeleton variant="text" width="55%" height={20} />
                          </Stack>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: 1.5,
                            }}
                          >
                            <Skeleton variant="rounded" width={58} height={22} />
                            <Skeleton variant="rounded" width={48} height={22} />
                            <Skeleton variant="rounded" width={64} height={22} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  }

                  const card = item.card;
                  return (
                    <Box
                      key={item.id ?? `masonry-item-${index}`}
                      data-masonry-id={item.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenPreview(card.fileType, card.fileId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleOpenPreview(card.fileType, card.fileId);
                        }
                      }}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        width: "100%",
                        cursor: "pointer",
                        outline: "none",
                        "&:focus-visible": {
                          boxShadow:
                            "0 0 0 3px rgba(194, 24, 91, 0.25), 0 2px 10px rgba(0, 0, 0, 0.08)",
                        },
                        "&:hover .designer-files-card-action, &:focus-within .designer-files-card-action":
                          {
                            opacity: 1,
                            transform: "translateY(0)",
                            pointerEvents: "auto",
                          },
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <Box
                          component="img"
                          src={card.imageUrl}
                          alt={card.altText}
                          loading={showAnySkeleton ? "eager" : "lazy"}
                          onLoad={() => handleCardImageLoad(card.id)}
                          onError={() => handleCardImageLoad(card.id)}
                          sx={{
                            width: "100%",
                            display: "block",
                          }}
                        />

                        <Button
                          className="designer-files-card-action"
                          size="small"
                          variant="outlined"
                          startIcon={<FavoriteBorderIcon sx={{ fontSize: 16 }} />}
                          onClick={(event) => handleOpenSavePopover(event, card)}
                          disabled={!canSaveToIdeaBooks || !card.imageId}
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            textTransform: "none",
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            color: "#1F2933",
                            borderColor: "#E5E7EB",
                            fontWeight: 400,
                            borderRadius: "8px",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                            opacity: 0,
                            pointerEvents: "none",
                            transform: "translateY(-4px)",
                            transition: "opacity 0.2s ease, transform 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#fff",
                              borderColor: "#D1D5DB",
                            },
                          }}
                        >
                          Save
                        </Button>
                      </Box>

                      <Box sx={{ p: 2.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#1F2933",
                            mb: 1,
                          }}
                        >
                          {card.title}
                        </Typography>

                        {card.tags.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: 1.5,
                            }}
                          >
                            {card.tags.map((tag) => (
                              <Chip
                                key={`${card.id}-${tag}`}
                                label={tag}
                                size="small"
                                sx={{
                                  borderRadius: "999px",
                                  backgroundColor: "#F3F4F6",
                                  color: "#6B7280",
                                  fontSize: "12px",
                                  textTransform: "lowercase",
                                  height: 24,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                </Masonry>
              </Box>
              {canLoadMore && (
                <Box
                  ref={loadMoreRef}
                  sx={{
                    height: 56,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  {isLoadingMore ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Button variant="text" onClick={handleRequestAppend}>
                      Load more
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>

        <ProjectPreviewDialog
          open={previewOpen}
          onClose={handleClosePreview}
          fileType={previewDialogType}
          fileId={previewFileId}
        />
        <SaveToIdeaBookPopover
          open={Boolean(saveAnchorEl)}
          anchorEl={saveAnchorEl}
          onClose={handleCloseSavePopover}
          homeownerId={user?.id ?? null}
          imageId={saveCard?.imageId ?? null}
          imageUrl={saveCard?.imageUrl ?? null}
        />

        {showJoinCta && (
          <Box
            sx={{
              textAlign: "center",
              mt: { xs: 6, md: 8 },
              mb: { xs: 6, md: 10 },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Ready to Transform Your Space?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              To unlock all features and enjoy full access, please create an account
              or join now for our paid membership!
            </Typography>
            <Button
              component={Link}
              to={Pages.SIGNUP}
              variant="contained"
              sx={{
                backgroundColor: "#C2185B",
                textTransform: "none",
                px: 6,
                borderRadius: "8px",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#A8124F",
                },
              }}
            >
              Join Now
            </Button>
            {showSignIn && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Already have an account?{" "}
                <Button
                  component={Link}
                  to={Pages.LOGIN}
                  variant="text"
                  sx={{ textTransform: "none", color: "#C2185B", p: 0, minWidth: 0 }}
                >
                  Sign in
                </Button>
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "100%",
            overflowX: "hidden",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              {showDrawerBack ? (
                <IconButton onClick={handleDrawerBack} sx={{ color: "#111827" }}>
                  <ArrowBackIosNewIcon />
                </IconButton>
              ) : (
                <Box sx={{ width: 40 }} />
              )}
              <IconButton onClick={handleDrawerClose} sx={{ color: "#111827" }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
              {drawerTitle}
            </Typography>
            {drawerSubtitle && (
              <Typography variant="body2" sx={{ mt: 0.5, color: "#6B7280" }}>
                {drawerSubtitle}
              </Typography>
            )}
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: "auto", px: 3, pb: 2 }}>
            {drawerBody}
          </Box>
          {showDrawerActions ? (
            <Box sx={{ px: 3, pb: 3, pt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                sx={{
                  backgroundColor: "#111827",
                  color: "#fff",
                  borderRadius: "10px",
                  py: 1.4,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#111827",
                  },
                }}
              >
                Apply
              </Button>
              <Button
                onClick={handleClearFilters}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  color: "#6B7280",
                  width: "100%",
                  textDecoration: "underline",
                }}
              >
                Clear Filters
              </Button>
            </Box>
          ) : (
            <Box sx={{ px: 3, pb: 3, pt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleDrawerClose}
                sx={{
                  backgroundColor: "#111827",
                  color: "#fff",
                  borderRadius: "10px",
                  py: 1.4,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#111827",
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};
